import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import translate from "../../../i18n/translate";
import { SERVICE_CATEGORIES } from "../../../shared/constants/serviceCategories";
import {
  getServiceCategories,
  updateServiceCategory,
} from "../../../actions/serviceCategoriesActions";

// Switches the main-menu services on and off. A disabled service is greyed out
// in the menu, its routes are not registered, and its API endpoints answer 403.
const ServiceCategoriesSettings = ({
  isDarktheme,
  serviceCategories,
  getServiceCategories,
  updateServiceCategory,
}) => {
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [categories, setCategories] = useState({});

  useEffect(() => {
    getServiceCategories().then(() => setLoading(false));
  }, [getServiceCategories]);

  useEffect(() => {
    if (serviceCategories) {
      setCategories(serviceCategories);
    }
  }, [serviceCategories]);

  const handleToggle = async (code, nextEnabled) => {
    setSavingCode(code);

    // Optimistic, then reconciled against the server below.
    setCategories((prev) => ({ ...prev, [code]: nextEnabled }));

    const ok = await updateServiceCategory(code, nextEnabled);

    if (ok) {
      await getServiceCategories();
    } else {
      setCategories((prev) => ({ ...prev, [code]: !nextEnabled }));
    }

    setSavingCode(null);
  };

  const enabledCount = SERVICE_CATEGORIES.filter(
    ({ code }) => categories[code] !== false
  ).length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3, color: isDarktheme ? "#fff" : "inherit" }}>
      <Typography variant="h5" sx={{ marginBottom: 1 }}>
        {translate("Services")}
      </Typography>

      <Typography variant="body2" sx={{ marginBottom: 3, opacity: 0.75 }}>
        {translate("servicesSettingsHint")}
      </Typography>

      {enabledCount === 0 && (
        <Alert severity="warning" sx={{ marginBottom: 3 }}>
          {translate("servicesAllDisabledWarning")}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate("Service")}</TableCell>
              <TableCell align="right">{translate("Status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SERVICE_CATEGORIES.map(({ code, labelKey }) => {
              const enabled = categories[code] !== false;

              return (
                <TableRow key={code} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>
                      {translate(labelKey)}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {code}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {savingCode === code ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={enabled}
                        onChange={(e) => handleToggle(code, e.target.checked)}
                        color="success"
                        inputProps={{ "aria-label": code }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const mapStateToProps = ({
  darkTheme: { isDarktheme },
  serviceCategories: { map },
}) => ({
  isDarktheme,
  serviceCategories: map,
});

export default connect(mapStateToProps, {
  getServiceCategories,
  updateServiceCategory,
})(ServiceCategoriesSettings);
